import React, { useEffect, useState } from 'react';
import {
    Card, CardContent, Typography, Table, TableHead, TableRow,
    TableCell, TableBody, Button, makeStyles, TablePagination, Grid
} from '@material-ui/core';
import { Calendar } from 'antd';
import moment from 'moment';
import axios from 'axios';
import { useGetIdentity } from 'react-admin';

const useStyles = makeStyles(() => ({
    card: {
        margin: '2rem auto',
        width: '90%',
        maxWidth: '1200px',
    },
    confirmed: { color: 'green', fontWeight: 'bold' },
    requested: { color: 'orange', fontWeight: 'bold' },
    rejected: { color: 'red', fontWeight: 'bold' },
    cancelled: { color: 'gray', fontWeight: 'bold' },
    infoBox: { marginBottom: '1rem' },
    low: { color: 'red', fontWeight: 'bold' },
    medium: { color: 'orange', fontWeight: 'bold' },
    high: { color: 'green', fontWeight: 'bold' },
    cancelButton: {
        marginLeft: '1rem',
        backgroundColor: '#f44336',
        color: 'white',
        '&:hover': { backgroundColor: '#d32f2f' }
    }
}));

const MyHolidays = () => {
    const classes = useStyles();
    const { identity, isLoading } = useGetIdentity();
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState('');
    const [allConfirmed, setAllConfirmed] = useState([]);
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [page, setPage] = useState(0);
    const [page2, setPage2] = useState(0);
    const rowsPerPage = 25;

    const fetchSummary = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/holidays/summary/employee/1`);
            setSummary(res.data);
        } catch {
            setError('Error loading holidays.');
        }
    };

    const fetchAllConfirmed = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/holidays/confirmed-with-names`);
            setAllConfirmed(res.data);
        } catch {
            console.error('Error loading confirmed holidays.');
        }
    };

    useEffect(() => {
        if (identity?.id) {
            fetchSummary();
            fetchAllConfirmed();
        }
    }, [identity]);

    const handleCancel = async (holidayId) => {
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/holidays/cancel/${holidayId}`);
            await fetchSummary();
            await fetchAllConfirmed();
        } catch {
            alert('Error cancelling holiday.');
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'CONFIRMADA': return classes.confirmed;
            case 'SOLICITADA': return classes.requested;
            case 'RECHAZADA': return classes.rejected;
            case 'CANCELADA': return classes.cancelled;
            default: return '';
        }
    };

    const getBalanceClass = (remaining) => {
        if (remaining <= 5) return classes.low;
        if (remaining <= 10) return classes.medium;
        return classes.high;
    };

    const renderBalance = () => {
        if (!summary) return null;
        if (summary.remainingDays != null) {
            const remaining = summary.remainingDays.toFixed(1);
            return <Typography className={`${classes.infoBox} ${getBalanceClass(remaining)}`}><strong>Remaining Days:</strong> {remaining}</Typography>;
        }
        if (summary.remainingHours != null) {
            const remaining = summary.remainingHours.toFixed(1);
            return <Typography className={`${classes.infoBox} ${getBalanceClass(remaining)}`}><strong>Remaining Hours:</strong> {remaining}</Typography>;
        }
        return null;
    };

    const renderCalendarCell = (value) => {
        const dayStr = value.format('YYYY-MM-DD');
        const holidaysThisDay = allConfirmed.filter(h =>
            moment(h.holidayDate).format('YYYY-MM-DD') === dayStr
        );
        return (
            <ul style={{ listStyle: 'none', padding: 0 }}>
                {holidaysThisDay.map((h, i) => (
                    <li key={i}>{h.employeeName}</li>
                ))}
            </ul>
        );
    };

    const holidays = [
        ...(summary?.confirmedHolidays || []),
        ...(summary?.requestedHolidays || []),
        ...(summary?.rejectedHolidays || []),
        ...(summary?.cancelledHolidays || [])
    ];

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangePage2 = (_, newPage) => setPage2(newPage);

    if (isLoading || !identity) return <p>Loading...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    return (
        <>
            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="h5">My Holidays</Typography>
                    {renderBalance()}
                    {holidays.length === 0 ? (
                        <Typography>No holidays registered.</Typography>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center"><strong>Date</strong></TableCell>
                                        <TableCell align="center"><strong>Status</strong></TableCell>
                                        <TableCell align="center"><strong>Action</strong></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {holidays.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map(h => (
                                        <TableRow key={h.id}>
                                            <TableCell align="center">{new Date(h.holidayDate).toLocaleDateString()}</TableCell>
                                            <TableCell align="center" className={getStatusClass(h.status)}>{h.status}</TableCell>
                                            <TableCell align="center">
                                                {(h.status === 'SOLICITADA' || h.status === 'CONFIRMADA') && (
                                                    <Button size="small" className={classes.cancelButton} onClick={() => handleCancel(h.id)}>
                                                        Cancel
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[rowsPerPage]}
                                count={holidays.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                component="div"
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <Card className={classes.card}>
                <CardContent>
                    <Grid container alignItems="center" justifyContent="space-between" style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                        <Grid item>
                            <Typography variant="h6">Confirmed Holidays (All Employees)</Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setCalendarVisible(!calendarVisible)}
                            >
                                {calendarVisible ? 'View as Table' : 'View as Calendar'}
                            </Button>
                        </Grid>
                    </Grid>

                    {calendarVisible ? (
                        <Calendar dateCellRender={renderCalendarCell} />
                    ) : allConfirmed.length === 0 ? (
                        <Typography>No confirmed holidays available.</Typography>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center"><strong>Employee</strong></TableCell>
                                        <TableCell align="center"><strong>Date</strong></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {allConfirmed.slice(page2 * rowsPerPage, (page2 + 1) * rowsPerPage).map(h => (
                                        <TableRow key={h.id}>
                                            <TableCell align="center">{h.employeeName}</TableCell>
                                            <TableCell align="center">{new Date(h.holidayDate).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[rowsPerPage]}
                                count={allConfirmed.length}
                                page={page2}
                                onPageChange={handleChangePage2}
                                rowsPerPage={rowsPerPage}
                                component="div"
                            />
                        </>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default MyHolidays;
